import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import dayjs from "dayjs"

import "chartjs-adapter-moment"

import { formatKpiValue } from "../../../Components/KpiTabs/formatKpiValue"
import { INCOGNITO_MASK } from "../../../Components/Private"
import { isIncognitoActive } from "../../../Utils/incognito"
import { baseOptions, externalDarkTooltip } from "../../DashboardPage/components/chartsOptions"
import { getGradient } from "../../DashboardPage/components/chartsPlagins"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const TOOLTIP_DATE_FORMAT = "MMM D, YYYY"

const LINE_COLOR = "#225AEA"
const COMPARISON_LINE_COLOR = "#A1A1AA"

// A rate's axis stays inside 0–100 on multiples of ten (RCA-5-06); chart.js would otherwise pad
// a 98% peak up to 105%.
const clampPercentAxis = (scale) => {
  const max = Math.min(100, Math.ceil(scale.max / 10) * 10)
  const min = Math.max(0, Math.floor(scale.min / 10) * 10)
  scale.max = max > min ? max : Math.min(100, min + 10)
  scale.min = max > min ? min : scale.max - 10
}

// Options for the KPI trend line under the tabs. `title` is the selected tab's own label —
// the chart has no visible title, but the tooltip leads with it, which is the only place
// the reader is told which metric they are hovering.
export const kpiTrendOptions = ({ theme, title, prefix, suffix }) => {
  const base = baseOptions(theme)
  const format = formatKpiValue({ prefix, suffix })
  // Drawn by chart.js outside React, hence the live Incognito mirror rather than <Private>.
  const formatValue = (value) => (isIncognitoActive() ? INCOGNITO_MASK : format(value))

  const headerStyle = "font-weight:500; color:#7A7A83; padding-bottom:2px;"
  const totalStyle = "font-weight:500; color:white; padding:6px 0;"
  const compareRowStyle =
    "display:flex; align-items:center; gap:8px; color:white; font-weight:500; padding:6px 0;"
  const dashStyle =
    `display:inline-block; width:14px; height:0; border-top:1.5px dashed ${COMPARISON_LINE_COLOR};` +
    " flex-shrink:0;"

  return {
    ...base,
    interaction: { mode: "index", intersect: false },
    plugins: {
      "vertical-line-plugin": { theme },
      title: { display: false },
      legend: { display: false },
      tooltip: {
        enabled: false,
        callbacks: {
          title: (context) => {
            const point = context?.[0]
            const date = point?.label ? dayjs(point.label).format(TOOLTIP_DATE_FORMAT) : ""

            const lines = [
              `__RAW__<div style="${headerStyle}">${title}</div>`,
              `__RAW__<div style="${headerStyle}">${date}</div>`,
              `__RAW__<div style="${totalStyle}">${formatValue(point?.parsed?.y)}</div>`
            ]

            // The dashed line has no legend to explain it, so the tooltip is the only place
            // the comparison is named — by its own day, not the hovered one.
            const comparisonPoint = context?.find((entry) => entry.dataset?.isComparison)
            if (comparisonPoint) {
              const rawDate = comparisonPoint.dataset?.comparisonLabels?.[comparisonPoint.dataIndex]
              const comparisonDate = rawDate
                ? dayjs(rawDate).format(TOOLTIP_DATE_FORMAT)
                : comparisonPoint.dataset?.label || ""

              lines.push(
                `__RAW__<div style="${headerStyle} padding-top:8px;">${comparisonDate}</div>`
              )
              lines.push(
                `__RAW__<div style="${compareRowStyle}"><span style="${dashStyle}"></span>` +
                  `<span>${formatValue(comparisonPoint.parsed?.y)}</span></div>`
              )
            }

            return lines
          }
        },
        external: externalDarkTooltip({ theme })
      }
    },
    scales: {
      ...base.scales,
      x: {
        ...base.scales.x,
        ticks: { ...base.scales.x.ticks, maxTicksLimit: 10, autoSkip: true, maxRotation: 0 }
      },
      y: {
        ...base.scales.y,
        ...(suffix === "%" ? { afterDataLimits: clampPercentAxis } : {}),
        // Counts and percentages are whole; a fractional tick prints the same label twice.
        ticks: { ...base.scales.y.ticks, precision: 0, callback: (value) => formatValue(value) }
      }
    }
  }
}

export const buildKpiDataset = ({ label, data, pointCount }) => ({
  label,
  data,
  borderColor: LINE_COLOR,
  borderWidth: 2,
  yAxisID: "y",
  // Win rate is null in buckets with nothing resolved; span those gaps so the line stays
  // continuous instead of fragmenting. Count series carry no nulls, so this is a no-op there.
  spanGaps: true,
  fill: true,
  backgroundColor: (context) => {
    const { ctx, chartArea } = context.chart
    if (!chartArea) return undefined
    return getGradient(ctx, chartArea)
  },
  ...(pointCount === 1
    ? { pointRadius: 4, pointHoverRadius: 6 }
    : { pointRadius: 0, pointHoverRadius: 6 }),
  pointHoverBorderWidth: 2,
  pointHoverBackgroundColor: "white"
})

// Trustworthy only point-for-point with the axis: the overlay is plotted against the current
// window's labels by index, so a mismatched length would line the dashed line up with the
// wrong days. Dropped rather than guessed at.
export const buildKpiComparisonOverlay = ({ labels, series, comparisonLabels, label }) => {
  if (!series?.length || series.length !== labels?.length) return []

  return [
    {
      label,
      data: series,
      isComparison: true,
      comparisonLabels,
      borderColor: COMPARISON_LINE_COLOR,
      borderWidth: 1.5,
      borderDash: [6, 6],
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHoverBorderWidth: 2,
      pointHoverBorderColor: COMPARISON_LINE_COLOR,
      pointHoverBackgroundColor: "white",
      yAxisID: "y",
      spanGaps: true,
      fill: false,
      order: 2
    }
  ]
}
